import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { Blockquote, Spinner } from '@ui';
import { snakeCase, sumBy } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '~/lib/cn';
import type { ExtendedSlot } from '../Views/TableView/types';

interface Props {
    slot: ExtendedSlot;
    kiosk?: boolean;
    summaryEndpoint?: string;
    requestData?: Record<string, any>;
    /** Shown instead of an empty block while the form still lacks the data to price the booking */
    pendingPlaceholder?: ReactNode;
}

const convertToCurrencyString = (value: number) => {
    if (isNaN(value) || value == 0) return 'kostenlos';

    const options = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };

    return value.toLocaleString('de-DE', options) + ' €';
};

interface SummaryResponse {
    data: {
        price: {
            description: string;
            price: number;
            price_per_hour: number;
            taxes: number | null;
        };
        prices: {
            description: string;
            price: number;
        }[];
        discounts: {
            description: string;
            discount: number;
        }[];
        old_total?: number;
        new_total?: number;
        delta?: number;
    };
}

const Row = ({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) => (
    <div className={cn('tw-flex tw-items-baseline tw-justify-between tw-gap-4 tw-text-sm', className)}>
        <span className="tw-min-w-0 tw-truncate tw-text-accents-7">{label}</span>
        <span className="tw-shrink-0 tw-whitespace-nowrap tw-font-semibold tw-tabular-nums tw-text-accents-9">
            {value}
        </span>
    </div>
);

const TotalRow = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
    <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-pt-3">
        <span className="tw-font-semibold tw-text-accents-9">{label}</span>
        <span className="tw-whitespace-nowrap tw-text-xl tw-font-bold tw-tabular-nums tw-text-accents-9">{value}</span>
    </div>
);

const BookingSummary = ({ slot, kiosk, summaryEndpoint, requestData, pendingPlaceholder }: Props) => {
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<SummaryResponse>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const { watch } = useFormContext();
    const debouncedData = useDebouncedValue(watch(), 500, true);
    const prices = data?.data.prices ?? [];
    const discounts = data?.data.discounts ?? [];
    const hasSummaryRows = prices.length > 0 || discounts.length > 0;

    const fetchSummary = useCallback(() => {
        if (!debouncedData?.user_name || !debouncedData?.user_email) return;
        const trainerId = Number(debouncedData?.trainer_id);
        const hasValidTrainerId = Number.isFinite(trainerId) && trainerId > 0;
        setIsLoading(true);
        setErrorMessage(undefined);
        return api({
            url: summaryEndpoint ?? `space-manager/space/${slot.spaceId}/summary`,
            method: 'POST',

            data: { ...debouncedData, ...(hasValidTrainerId ? { trainer_id: trainerId } : {}), kiosk, ...requestData },
            muted: true,
            onBefore: () => setIsLoading(false),
            onSuccess: (responseData) => {
                setData(responseData);
                setErrorMessage(undefined);
            },
            onFailure: (error) => {
                setData(undefined);
                setErrorMessage(error?.data?.message ?? 'Die Preiszusammenfassung konnte nicht berechnet werden.');
            }
        });
    }, [slot, debouncedData, kiosk, summaryEndpoint, requestData]);

    useEffect(() => {
        void fetchSummary();
    }, [fetchSummary]);

    const oldTotal = data?.data.old_total;
    const newTotal = data?.data.new_total;
    const delta = data?.data.delta;

    const total: number = useMemo(() => {
        if (newTotal !== undefined && newTotal !== null) return Number(newTotal);

        let total = sumBy(prices, (v) => Number(v.price));

        discounts?.forEach(({ discount }) => {
            total = total - (total * discount) / 100;
        });

        return total;
    }, [prices, discounts, newTotal]);

    // the summary needs an identity before the server can price anything, and anonymous
    // bookers only supply one a step later — say so instead of showing an empty column
    const isPending = !data && !errorMessage;

    if (errorMessage)
        return (
            <Blockquote color="warning" className="tw-mb-0">
                {errorMessage}
            </Blockquote>
        );

    if (isPending)
        return (
            <div className="tw-card-bordered tw-flex tw-items-center tw-gap-3 tw-border-dashed tw-bg-transparent tw-text-sm tw-text-accents-6">
                {isLoading ? (
                    <Spinner color="success" />
                ) : (
                    <FontAwesomeIcon icon={faReceipt} className="tw-w-4 tw-shrink-0 tw-text-accents-4" />
                )}
                <span>{pendingPlaceholder}</span>
            </div>
        );

    return (
        // a recalculation only dims the last numbers instead of swapping them for a spinner —
        // the card runs on every keystroke and would otherwise flicker with each one
        <div
            className={cn(
                'tw-card-bordered tw-flex tw-flex-col tw-gap-2 tw-transition-opacity tw-duration-200',
                isLoading && 'tw-opacity-50'
            )}
        >
            {hasSummaryRows && (
                <div className="tw-flex tw-flex-col tw-gap-2 tw-pb-1">
                    {prices.map(({ description, price }) => (
                        <Row
                            key={`${price}_${snakeCase(description)}`}
                            label={description}
                            value={convertToCurrencyString(Number(price))}
                        />
                    ))}
                    {discounts.map(({ description, discount }) => (
                        <Row
                            key={`${discount}_${snakeCase(description)}`}
                            className="tw-text-success [&>span]:tw-text-success"
                            label={description}
                            value={`− ${Number(discount)} %`}
                        />
                    ))}
                </div>
            )}
            {typeof delta !== 'number' ? (
                <TotalRow label="Gesamt" value={convertToCurrencyString(Number(total))} />
            ) : (
                <>
                    {typeof oldTotal === 'number' && (
                        <Row label="Bisher" value={convertToCurrencyString(Number(oldTotal))} />
                    )}
                    {typeof newTotal === 'number' && (
                        <Row label="Neu" value={convertToCurrencyString(Number(newTotal))} />
                    )}
                    <TotalRow
                        label="Differenz"
                        value={`${Number(delta).toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })} €`}
                    />
                </>
            )}
        </div>
    );
};

export default BookingSummary;
